// src/components/admin/AdminDashboardHome.jsx
import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    List,
    Progress,
    Row,
    Space,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {useNavigate} from "react-router-dom";
import {
    CalendarOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    ReloadOutlined,
    SmileOutlined,
    TeamOutlined,
    BarChartOutlined,
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

import {
    useTotalChats,
    useOpenChats,
    useClosedChats,
    useAverageWaitTime,
    useAverageSatisfaction,
    useLiveSnapshot,
    useLiveOperators,
} from "./admin-dashboard-home-query";
import {stateMeta} from "../../../utils/chatState";

const {Text} = Typography;

// ---------- helpers (تاریخ و عدد فارسی) ----------
const toPersianDateObject = (d) =>
    d ? new DateObject({date: new Date(d), calendar: persian, locale: persian_fa}) : null;

const fmtPersian = (d, token = "YYYY/MM/DD HH:mm:ss") => {
    const obj = toPersianDateObject(d);
    return obj ? obj.format(token) : "-";
};

const enToFaDigits = (v) =>
    String(v ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

const faDateWithHour = (d) => fmtPersian(d, "HH:mm YYYY/MM/DD");
const faShortDate = (d) => fmtPersian(d, "MM/DD"); // برای محور X نمودارها

const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

// برچسب و رنگ وضعیت‌ها از نقشه‌ی مشترک src/utils/chatState.js خوانده می‌شود.

// سری‌های نمودار انباشته‌ی چت‌های باز (رنگ‌ها هم‌راستا با حباب‌های کنسول اپراتور)
const OPEN_SERIES = [
    {key: "agent", label: "ایجنت", color: "#5c6bc0"},
    {key: "operator", label: "اپراتور", color: "#43a047"},
    {key: "waiting", label: "در صف", color: "#faad14"},
];

// ---------- کنسول زنده ----------
// «چند دقیقه است که منتظر است» — رنگ‌ها با rgba نوشته شده‌اند تا در تم تیره هم خوانا بماند.
const DANGER = "#ff4d4f";

const relativeAge = (iso, nowMs) => {
    if (!iso) return "—";
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return "—";
    const minutes = Math.floor((nowMs - then) / 60000);
    if (minutes < 1) return "کمتر از یک دقیقه";
    if (minutes < 60) return `${enToFaDigits(minutes)} دقیقه`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (hours < 24) {
        return rest
            ? `${enToFaDigits(hours)} ساعت و ${enToFaDigits(rest)} دقیقه`
            : `${enToFaDigits(hours)} ساعت`;
    }
    return `${enToFaDigits(Math.floor(hours / 24))} روز`;
};

const KpiTile = ({label, value, hint, danger, loading}) => (
    <Card
        loading={loading}
        bodyStyle={{padding: 12}}
        className="shadow-sm rounded-2xl h-full"
        style={{
            border: `1px solid ${danger ? DANGER : "var(--npe-hairline, #f0f0f0)"}`,
            background: danger ? "rgba(255,77,79,0.10)" : undefined,
        }}
    >
        <Space direction="vertical" size={0} className="w-full">
            <Text type={danger ? undefined : "secondary"} className="text-xs"
                  style={danger ? {color: DANGER} : undefined}>
                {label}
            </Text>
            <Text
                className="text-xl md:text-2xl font-bold"
                style={danger ? {color: DANGER} : undefined}
            >
                {value}
            </Text>
            {hint ? (
                <Text type="secondary" style={{fontSize: 11}}>
                    {hint}
                </Text>
            ) : null}
        </Space>
    </Card>
);

const AdminDashboardHome = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    // ------ کوئری‌ها (همه startDate اختیاری = 7 روز اخیر) ------
    const {
        data: totalChatsData,
        isLoading: loadingTotal,
        refetch: refetchTotal,
    } = useTotalChats(undefined);

    const {
        data: openChatsData,
        isLoading: loadingOpen,
        refetch: refetchOpen,
    } = useOpenChats(undefined);

    const {
        data: closedChatsData,
        isLoading: loadingClosed,
        refetch: refetchClosed,
    } = useClosedChats(undefined);

    const {
        data: avgWaitData,
        isLoading: loadingWait,
        refetch: refetchWait,
    } = useAverageWaitTime(undefined);

    const {
        data: avgSatisfactionData,
        isLoading: loadingSat,
        refetch: refetchSat,
    } = useAverageSatisfaction(undefined);

    // ------ کنسول زنده (وضعیت همین لحظه، هر ۱۵ ثانیه) ------
    const {
        data: liveSnapshot,
        isLoading: loadingLive,
        refetch: refetchLive,
    } = useLiveSnapshot();

    const {
        data: liveOperatorsData,
        isLoading: loadingOps,
        refetch: refetchOps,
    } = useLiveOperators();

    // برچسب‌های «چند دقیقه منتظر است» باید بدون تغییر داده هم جلو بروند.
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 15000);
        return () => clearInterval(id);
    }, []);

    // ------ تبدیل DTOها به داده‌ی کارت‌ها و نمودارها ------

    // ۱+۲) ترکیب چت‌های باز: ایجنت / اپراتور / در صف — هر سه در همان DashboardOpenChatsDTO
    // می‌آیند؛ تا امروز فقط دو تای آخر خوانده می‌شد و سهم ایجنت دور ریخته می‌شد.
    const openChatsStats = useMemo(() => {
        const days = openChatsData?.dayData || [];
        return {
            agent: openChatsData?.agentChats ?? 0,
            operator: openChatsData?.operatorChats ?? 0,
            waiting: openChatsData?.waitingChats ?? 0,
            lastUpdate: days.length ? days[days.length - 1].date : null,
            chart: days.map((d) => ({
                label: faShortDate(d.date),
                agent: d.agentChats ?? 0,
                operator: d.operatorChats ?? 0,
                waiting: d.waitingChats ?? 0,
            })),
        };
    }, [openChatsData]);

    const waitingTimeStats = useMemo(() => {
        const days = avgWaitData?.dayAverages || [];
        return {
            // کل میانگین → ثانیه به دقیقه
            avgMinutes: avgWaitData?.overallAverage
                ? avgWaitData.overallAverage / 60
                : 0,

            // نمودار روزانه → ثانیه به دقیقه
            chart: days.map((d) => ({
                label: faShortDate(d.date),
                value: d.averageWaitTime
                    ? d.averageWaitTime / 60
                    : 0,
            })),
        };
    }, [avgWaitData]);

    // ۴) مجموع چت‌ها از DashboardTotalChatsDTO (به‌جای unread mock)
    const totalChatsStats = useMemo(() => {
        const days = totalChatsData?.dayData || [];
        return {
            count: totalChatsData?.totalChats ?? 0,
            lastUpdate: days.length ? days[days.length - 1].date : null,
            chart: days.map((d) => ({
                label: faShortDate(d.date),
                value: d.chatCount ?? 0,
            })),
        };
    }, [totalChatsData]);

    const satisfactionStats = useMemo(() => {
        const days = avgSatisfactionData?.dayAverages || [];
        return {
            averageScore: avgSatisfactionData?.overallAverage ?? 0,
            maxScore: 5,
            chart: days.map((d) => {
                const val = d.averageSatisfaction ?? 0;
                return {
                    label: faShortDate(d.date),
                    value: Number(val.toFixed(1)), // ✅ یک رقم اعشار
                };
            }),
        };
    }, [avgSatisfactionData]);

    const resolvedStats = useMemo(() => {
        const days = closedChatsData?.dayData || [];
        const agentFinished = closedChatsData?.agentFinishedChats ?? 0;
        const operatorFinished = closedChatsData?.operatorFinishedChats ?? 0;
        const timeOut = closedChatsData?.timeOutChats ?? 0;
        const answered = agentFinished + operatorFinished;
        return {
            count: closedChatsData?.total ?? 0,
            agentFinished,
            operatorFinished,
            timeOut,
            // نرخ حل توسط ایجنت: سهم گفت‌وگوهایی که بدون اپراتور بسته شده‌اند (رهاشده‌ها شمرده نمی‌شوند)
            containment: answered ? (agentFinished / answered) * 100 : 0,
            chart: days.map((d) => ({
                label: faShortDate(d.date),
                value: d.total ?? 0,
            })),
        };
    }, [closedChatsData]);

    const live = useMemo(() => {
        const byChannel = liveSnapshot?.byChannel || {};
        const sum = (ch) => {
            const c = byChannel[ch] || {};
            return (c.AGENT ?? 0) + (c.WAITING ?? 0) + (c.OPERATOR ?? 0);
        };
        return {
            agentChats: liveSnapshot?.agentChats ?? 0,
            waitingChats: liveSnapshot?.waitingChats ?? 0,
            operatorChats: liveSnapshot?.operatorChats ?? 0,
            unassignedWaiting: liveSnapshot?.unassignedWaiting ?? 0,
            oldestWaitingAt: liveSnapshot?.oldestWaitingAt ?? null,
            oldestUnassignedAt: liveSnapshot?.oldestUnassignedAt ?? null,
            unreadChats: liveSnapshot?.unreadChats ?? 0,
            capacityExhausted: !!liveSnapshot?.capacityExhausted,
            maxChatsPerOperator: liveSnapshot?.maxChatsPerOperator ?? 0,
            telegram: sum("TELEGRAM"),
            web: sum("WEB"),
        };
    }, [liveSnapshot]);

    // مرتب‌سازی تخته‌ی اپراتورها: اول کسانی که در شیفت‌اند، بعد پربارترین‌ها.
    const liveOperators = useMemo(() => {
        const rows = Array.isArray(liveOperatorsData) ? liveOperatorsData : [];
        return [...rows].sort((a, b) => {
            if (!!b.onShift !== !!a.onShift) return b.onShift ? 1 : -1;
            return (b.activeChats ?? 0) - (a.activeChats ?? 0);
        });
    }, [liveOperatorsData]);

    const handleRefresh = () => {
        refetchLive();
        refetchOps();
        refetchTotal();
        refetchOpen();
        refetchClosed();
        refetchWait();
        refetchSat();
    };

    const anyLoading =
        loadingTotal ||
        loadingOpen ||
        loadingClosed ||
        loadingWait ||
        loadingSat ||
        loadingLive ||
        loadingOps;

    return (
        <div className="w-full h-full p-4 md:p-6 space-y-4" dir="rtl">
            {/* هدر داشبورد */}
            <Row align="middle" justify="space-between" className="mb-2">
                <Col>
                    <Space direction="vertical" size={0}>
                        <Text className="text-lg md:text-xl font-semibold">
                            داشبورد ادمین
                        </Text>
                        <Text type="secondary" className="text-xs md:text-sm">
                            نمای کلی از وضعیت تمام چت‌ها، صف‌ها و عملکرد اپراتورها
                        </Text>
                    </Space>
                </Col>
                <Col>
                    <Space>
                        <Tooltip title="آخرین بروزرسانی">
                            <Space className="text-xs md:text-sm text-gray-500">
                                <CalendarOutlined/>
                                {faDateWithHour(new Date())}
                            </Space>
                        </Tooltip>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={handleRefresh}
                            className="rounded-full"
                            loading={anyLoading}
                        >
                            بروزرسانی
                        </Button>
                    </Space>
                </Col>
            </Row>

            {/* ================= کنسول زنده: وضعیت همین لحظه (هر ۱۵ ثانیه) ================= */}
            {live.capacityExhausted && (
                <Alert
                    type="warning"
                    showIcon
                    message="همه اپراتورها پر هستند"
                    description={
                        live.maxChatsPerOperator
                            ? `هر اپراتور در حال حاضر تا سقف ${enToFaDigits(live.maxChatsPerOperator)} گفت‌وگو را در دست دارد؛ گفت‌وگوهای تازه در صف می‌مانند.`
                            : "گفت‌وگوهای تازه تا آزاد شدن ظرفیت در صف می‌مانند."
                    }
                />
            )}

            <Row gutter={[12, 12]}>
                <Col xs={12} sm={8} lg={6} xl={3}>
                    <KpiTile
                        loading={loadingLive}
                        label="در صف اکنون"
                        value={enToFaDigits(live.waitingChats)}
                        hint="در انتظار پاسخ اپراتور"
                    />
                </Col>
                <Col xs={12} sm={8} lg={6} xl={4}>
                    <KpiTile
                        loading={loadingLive}
                        label="قدیمی‌ترین انتظار"
                        value={relativeAge(live.oldestWaitingAt, nowMs)}
                        hint={
                            live.oldestWaitingAt
                                ? faDateWithHour(live.oldestWaitingAt)
                                : "صف خالی است"
                        }
                    />
                </Col>
                <Col xs={12} sm={8} lg={6} xl={4}>
                    <KpiTile
                        loading={loadingLive}
                        label="بدون اپراتور"
                        danger={live.unassignedWaiting > 0}
                        value={enToFaDigits(live.unassignedWaiting)}
                        hint={
                            live.unassignedWaiting > 0
                                ? `قدیمی‌ترین: ${relativeAge(live.oldestUnassignedAt, nowMs)}`
                                : "هیچ گفت‌وگویی بی‌صاحب نمانده"
                        }
                    />
                </Col>
                <Col xs={12} sm={8} lg={6} xl={3}>
                    <KpiTile
                        loading={loadingLive}
                        label="در حال پاسخ‌گویی"
                        value={enToFaDigits(live.operatorChats)}
                        hint="در دست اپراتورها"
                    />
                </Col>
                <Col xs={12} sm={8} lg={6} xl={3}>
                    <KpiTile
                        loading={loadingLive}
                        label="در اختیار ایجنت"
                        value={enToFaDigits(live.agentChats)}
                        hint="هنوز به اپراتور نرسیده"
                    />
                </Col>
                <Col xs={12} sm={8} lg={6} xl={3}>
                    <KpiTile
                        loading={loadingLive}
                        label="خوانده‌نشده"
                        value={enToFaDigits(live.unreadChats)}
                        hint="پیام بی‌پاسخ در صف/اپراتور"
                    />
                </Col>
                <Col xs={12} sm={8} lg={6} xl={4}>
                    <KpiTile
                        loading={loadingLive}
                        label="تلگرام : ویجت"
                        value={`${enToFaDigits(live.telegram)} : ${enToFaDigits(live.web)}`}
                        hint="گفت‌وگوهای باز به تفکیک کانال"
                    />
                </Col>
            </Row>

            {/* تخته‌ی زنده‌ی اپراتورها — همه‌ی اپراتورها، حتی آن‌ها که هنوز امتیازی نگرفته‌اند */}
            <Card
                loading={loadingOps}
                className="shadow-sm rounded-2xl border border-gray-100"
                title={
                    <Space>
                        <TeamOutlined/>
                        <span>اپراتورها — بار لحظه‌ای</span>
                    </Space>
                }
                extra={
                    <Space size={8}>
                        {live.maxChatsPerOperator ? (
                            <Tag className="rounded-full">
                                سقف هر اپراتور: {enToFaDigits(live.maxChatsPerOperator)}
                            </Tag>
                        ) : null}
                        <Button
                            type="link"
                            size="small"
                            onClick={() => navigate("/dashboard/operators/status")}
                        >
                            نمای کامل
                        </Button>
                    </Space>
                }
                bodyStyle={{padding: 16}}
            >
                {liveOperators.length ? (
                    <Row gutter={[12, 12]}>
                        {liveOperators.map((op) => {
                            const max = live.maxChatsPerOperator || 0;
                            const active = op.activeChats ?? 0;
                            const percent = max ? Math.min(100, (active / max) * 100) : 0;
                            const full = max > 0 && active >= max;
                            return (
                                <Col xs={24} sm={12} lg={8} key={op.operatorId}>
                                    <Card size="small" bodyStyle={{padding: 12}}
                                          className="rounded-xl h-full border border-gray-100">
                                        <Space direction="vertical" size={6} className="w-full">
                                            <Space className="w-full justify-between" align="start">
                                                <Space>
                                                    <Badge
                                                        dot
                                                        color={op.onShift ? "#52c41a" : "#bfbfbf"}
                                                        title={op.onShift ? "در شیفت" : "خارج از شیفت"}
                                                    >
                                                        <Avatar size="small" className="bg-indigo-500 text-white">
                                                            {getInitials(op.fullName)}
                                                        </Avatar>
                                                    </Badge>
                                                    <Space direction="vertical" size={0}>
                                                        <Text className="font-medium">{op.fullName}</Text>
                                                        <Text type="secondary" style={{fontSize: 11}}>
                                                            {op.onShift ? "در شیفت" : "خارج از شیفت"}
                                                        </Text>
                                                    </Space>
                                                </Space>
                                                {(op.unreadChats ?? 0) > 0 ? (
                                                    <Tooltip title="گفت‌وگوهای خوانده‌نشده">
                                                        <Tag color="red" className="rounded-full">
                                                            {enToFaDigits(op.unreadChats)} خوانده‌نشده
                                                        </Tag>
                                                    </Tooltip>
                                                ) : null}
                                            </Space>

                                            <Space className="w-full justify-between">
                                                <Text type="secondary" style={{fontSize: 12}}>
                                                    بار فعلی
                                                </Text>
                                                <Text style={{fontSize: 12}}>
                                                    {enToFaDigits(active)}
                                                    {max ? ` / ${enToFaDigits(max)}` : ""}
                                                </Text>
                                            </Space>
                                            <Progress
                                                percent={percent}
                                                showInfo={false}
                                                size="small"
                                                status={full ? "exception" : "normal"}
                                            />
                                            <Text type="secondary" style={{fontSize: 11}}>
                                                در صف این اپراتور: {enToFaDigits(op.queuedChats ?? 0)}
                                            </Text>
                                        </Space>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="اپراتوری یافت نشد"
                    />
                )}
            </Card>

            {/* ================= روند ۷ روز اخیر (تاریخی — نه وضعیت لحظه‌ای) ================= */}
            <Text type="secondary" className="text-xs md:text-sm block pt-2">
                روند ۷ روز اخیر
            </Text>

            {/* ردیف اول: کارت‌های خلاصه عددی (دید کلان) */}
            <Row gutter={[16, 16]}>
                {/* چت‌های باز: ایجنت / اپراتور / در صف (یک نمودار انباشته به‌جای دو کارت جدا) */}
                <Col xs={24} lg={12}>
                    <Card
                        loading={loadingOpen}
                        className="shadow-sm rounded-2xl border border-gray-100"
                        bodyStyle={{padding: 16}}
                    >
                        <Space className="w-full" align="start" direction="vertical">
                            <Space className="w-full justify-between">
                                <Space>
                                    <div
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                                        <TeamOutlined/>
                                    </div>
                                    <Text className="font-medium text-sm md:text-base">
                                        چت‌های باز — ایجنت / اپراتور / در صف
                                    </Text>
                                </Space>
                                <Tooltip title="آخرین روز داده">
                                    <Tag color="blue" className="rounded-full">
                                        {openChatsStats.lastUpdate
                                            ? faDateWithHour(openChatsStats.lastUpdate)
                                            : "-"}
                                    </Tag>
                                </Tooltip>
                            </Space>

                            <Space size={20} wrap className="mt-1">
                                {OPEN_SERIES.map((s) => (
                                    <Space key={s.key} direction="vertical" size={0}>
                                        <Space size={4}>
                                            <span
                                                style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: 2,
                                                    background: s.color,
                                                    display: "inline-block",
                                                }}
                                            />
                                            <Text type="secondary" className="text-xs">
                                                {s.label}
                                            </Text>
                                        </Space>
                                        <Text className="text-2xl md:text-3xl font-bold">
                                            {enToFaDigits(openChatsStats[s.key])}
                                        </Text>
                                    </Space>
                                ))}
                            </Space>

                            <Text type="secondary" className="text-xs">
                                سهم گفت‌وگوهایی که ایجنت پاسخ می‌دهد در برابر آنچه به اپراتور می‌رسد
                            </Text>

                            <div className="mt-2 h-32 w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={openChatsStats.chart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis
                                            dataKey="label"
                                            tickLine={false}
                                            fontSize={11}
                                        />
                                        <YAxis hide/>
                                        <RechartsTooltip
                                            formatter={(value, name) => [enToFaDigits(value), name]}
                                            labelFormatter={(label) => `تاریخ: ${label}`}
                                        />
                                        {OPEN_SERIES.map((s, idx) => (
                                            <Bar
                                                key={s.key}
                                                dataKey={s.key}
                                                name={s.label}
                                                stackId="open"
                                                fill={s.color}
                                                radius={
                                                    idx === OPEN_SERIES.length - 1
                                                        ? [6, 6, 0, 0]
                                                        : [0, 0, 0, 0]
                                                }
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Space>
                    </Card>
                </Col>

                {/* میانگین زمان انتظار کل سیستم */}
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        loading={loadingWait}
                        className="shadow-sm rounded-2xl border border-gray-100"
                        bodyStyle={{padding: 16}}
                    >
                        <Space className="w-full" align="start" direction="vertical">
                            <Space className="w-full justify-between">
                                <Space>
                                    <div
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-purple-100 text-purple-700">
                                        <ClockIcon/>
                                    </div>
                                    <Text className="font-medium text-sm md:text-base">
                                        میانگین زمان انتظار (سیستم)
                                    </Text>
                                </Space>
                                <Tag color="purple" className="rounded-full">
                                    دقیقه
                                </Tag>
                            </Space>

                            <Text className="text-2xl md:text-3xl font-bold mt-1">
                                {enToFaDigits(waitingTimeStats.avgMinutes.toFixed(2))}
                            </Text>

                            <Text type="secondary" className="text-xs">
                                میانگین زمانی که کاربران قبل از پاسخ، منتظر می‌مانند
                            </Text>

                            <div className="mt-2 h-24">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={waitingTimeStats.chart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                                        <XAxis
                                            dataKey="label"
                                            tickLine={false}
                                            fontSize={11}
                                        />
                                        <YAxis hide/>
                                        <RechartsTooltip/>
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            strokeWidth={2}
                                            dot={{r: 2}}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Space>
                    </Card>
                </Col>

                {/* مجموع چت‌ها (۷ روز اخیر) از DashboardTotalChatsDTO */}
                <Col xs={24} sm={12} lg={6}>
                    <Card
                        loading={loadingTotal}
                        className="shadow-sm rounded-2xl border border-gray-100"
                        bodyStyle={{padding: 16}}
                    >
                        <Space className="w-full" align="start" direction="vertical">
                            <Space className="w-full justify-between">
                                <Space>
                                    <div
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-100 text-red-600">
                                        <BarChartOutlined/>
                                    </div>
                                    <Text className="font-medium text-sm md:text-base">
                                        مجموع چت‌ها (۷ روز اخیر)
                                    </Text>
                                </Space>
                                <Tag color="red" className="rounded-full">
                                    ALL
                                </Tag>
                            </Space>

                            <Text className="text-2xl md:text-3xl font-bold mt-1">
                                {enToFaDigits(totalChatsStats.count)}
                            </Text>

                            <Space size={4}>
                                <Text type="secondary" className="text-xs">
                                    آخرین روز:
                                </Text>
                                <Text type="secondary" className="text-xs">
                                    {totalChatsStats.lastUpdate
                                        ? faDateWithHour(totalChatsStats.lastUpdate)
                                        : "-"}
                                </Text>
                            </Space>
                        </Space>
                    </Card>
                </Col>
            </Row>

            {/* ردیف دوم: رضایت کلی و حجم چت‌های حل‌شده */}
            <Row gutter={[16, 16]}>
                {/* رضایت کلی کاربران */}
                <Col xs={24} lg={12}>
                    <Card
                        loading={loadingSat}
                        className="shadow-sm rounded-2xl border border-gray-100"
                        title={
                            <Space>
                                <SmileOutlined className="text-yellow-500"/>
                                <span>رضایت کاربران (کل سیستم)</span>
                            </Space>
                        }
                        extra={
                            <Tag color="green" className="rounded-full">
                                میانگین امتیاز:{" "}
                                {enToFaDigits(satisfactionStats.averageScore.toFixed(1))}
                                /{enToFaDigits(satisfactionStats.maxScore)}
                            </Tag>
                        }
                        bodyStyle={{padding: 16}}
                    >
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} md={10}>
                                <Space direction="vertical">
                                    <Text type="secondary" className="text-xs">
                                        میانگین رضایت کاربران از چت‌ها
                                    </Text>
                                    <Text className="text-2xl font-bold">
                                        {enToFaDigits(
                                            satisfactionStats.averageScore.toFixed(2)
                                        )}{" "}
                                        از {enToFaDigits(5)}
                                    </Text>
                                    <Text type="secondary" className="text-xs leading-relaxed">
                                        این نمودار تغییرات میانگین امتیاز رضایت کاربران نسبت
                                        به پشتیبانی را در روزهای اخیر نشان می‌دهد.
                                    </Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={14}>
                                <div className="h-40">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={satisfactionStats.chart}>
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                vertical={false}
                                            />
                                            <XAxis
                                                dataKey="label"
                                                tickLine={false}
                                                fontSize={12}
                                            />
                                            <YAxis
                                                domain={[0, 5]}
                                                hide
                                                tickFormatter={(v) => v.toFixed(1)}
                                            />
                                            <RechartsTooltip
                                                formatter={(value) =>
                                                    value != null ? Number(value).toFixed(1) : value
                                                }
                                                labelFormatter={(label) => `تاریخ: ${label}`}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="value"
                                                strokeWidth={2}
                                                dot={{r: 3}}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* تعداد چت‌های پایان‌یافته (کل سیستم) */}
                <Col xs={24} lg={12}>
                    <Card
                        loading={loadingClosed}
                        className="shadow-sm rounded-2xl border border-gray-100"
                        title={
                            <Space>
                                <CheckCircleOutlined className="text-emerald-500"/>
                                <span>چت‌های پایان‌یافته (همه اپراتورها)</span>
                            </Space>
                        }
                        extra={
                            <Tooltip title="سهم گفت‌وگوهایی که ایجنت بدون نیاز به اپراتور بسته است">
                                <Tag color="green" className="rounded-full">
                                    حل توسط ایجنت:{" "}
                                    {enToFaDigits(resolvedStats.containment.toFixed(0))}٪
                                </Tag>
                            </Tooltip>
                        }
                        bodyStyle={{padding: 16}}
                    >
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} md={10}>
                                <Space direction="vertical" size={6}>
                                    <Text type="secondary" className="text-xs">
                                        روند تعداد چت‌های حل‌شده در روزهای اخیر
                                    </Text>
                                    <Text className="text-2xl font-bold">
                                        {enToFaDigits(resolvedStats.count)}
                                    </Text>
                                    {/* تفکیک پایان‌یافته‌ها؛ TIME_OUT تنها سیگنالِ رهاشدگی است که ثبت می‌شود. */}
                                    <Space size={6} wrap>
                                        <Tag
                                            color={stateMeta("AGENT_FINISHED").color}
                                            className="rounded-full"
                                        >
                                            {stateMeta("AGENT_FINISHED").label}:{" "}
                                            {enToFaDigits(resolvedStats.agentFinished)}
                                        </Tag>
                                        <Tag
                                            color={stateMeta("OPERATOR_FINISHED").color}
                                            className="rounded-full"
                                        >
                                            {stateMeta("OPERATOR_FINISHED").label}:{" "}
                                            {enToFaDigits(resolvedStats.operatorFinished)}
                                        </Tag>
                                        <Tooltip title="رها شده / بی‌پاسخ">
                                            <Tag
                                                color={stateMeta("TIME_OUT").color}
                                                className="rounded-full"
                                            >
                                                {stateMeta("TIME_OUT").label}:{" "}
                                                {enToFaDigits(resolvedStats.timeOut)}
                                            </Tag>
                                        </Tooltip>
                                    </Space>
                                    {/*<Button*/}
                                    {/*    type="link"*/}
                                    {/*    className="p-0"*/}
                                    {/*    onClick={() => navigate("/dashboard/chats")}*/}
                                    {/*>*/}
                                    {/*    مشاهده لیست کامل چت‌ها*/}
                                    {/*</Button>*/}
                                </Space>
                            </Col>
                            <Col xs={24} md={14}>
                                <div className="h-40">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={resolvedStats.chart}>
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                vertical={false}
                                            />
                                            <XAxis
                                                dataKey="label"
                                                tickLine={false}
                                                fontSize={12}
                                            />
                                            <YAxis hide/>
                                            <RechartsTooltip/>
                                            <Bar
                                                dataKey="value"
                                                radius={[8, 8, 0, 0]}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

        </div>
    );
};

export default AdminDashboardHome;

// آیکون ساعت ساده با SVG (برای میانگین زمان انتظار)
function ClockIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            className="w-4 h-4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
            <path
                d="M12 7v5l3 2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
